// ============================================
// CAPTURAIMPORTES.JS — la pantalla para recuperar los importes que faltan
// ============================================
// POR QUE EXISTE: 149 expedientes se facturaron sin cotizacion y sin apuntar el importe.
// Beep sabe QUE se facturo y con que folio de Aspel, pero no CUANTO valio. Capturarlos de
// uno en uno, entrando a cada expediente, son 131 navegaciones: eso no lo termina nadie.
//
// LAS CUATRO DECISIONES DEL DISEÑO, todas de Ricardo y todas con el mismo motivo —que esto
// se pueda terminar en media jornada con Aspel abierto al lado:
//
//   1. SE CAPTURA CON EL TECLADO, sin raton. Tabulador para bajar al siguiente renglon y
//      Enter para guardar y saltar. La mano no se levanta del teclado numerico.
//   2. SE GUARDA AL SALIR DE CADA CAMPO, no al final. Si se cierra el navegador en la fila
//      80, las 79 anteriores ya estan guardadas. No hay boton de "guardar todo" a proposito:
//      un boton final es justo lo que hace que se pierda el trabajo.
//   3. EL RENGLON DESAPARECE al guardarse. La lista mide lo que FALTA, asi que encogerse es
//      la barra de progreso. Se queda 1,2 s en verde para que se vea que entro.
//   4. AGRUPADO POR CLIENTE y con lo mas reciente arriba (el orden lo decide el servidor):
//      si la lista saltara de un cliente a otro, en Aspel habria que estar cambiando de
//      busqueda en cada renglon.
//
// LOS FOLIOS RAROS se marcan en ambar con lo que probablemente son. NO se corrigen solos:
// la cifra la pone una persona mirando Aspel, no una regla adivinando.
//
// CANDADO: el servidor exige `administracion.capturar-importes` (direccion pasa siempre).
// La pantalla no se dibuja sin el permiso, pero lo que manda es el 403 del backend.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from './config/api';
import { authHeaders } from './utils/storage';
import useResponsive from './hooks/useResponsive';

const PP = "'Poppins', sans-serif";
const AZUL = '#265F97';
const OSCURO = '#2e3338';
const GRIS = '#5e666e';
const GRIS_CLARO = '#9aa3ab';
const BORDE = '#e6e8ea';
const VERDE = '#1a7f4b';
const AMBAR_BORDE = '#f0d9a8';
const AMBAR_FONDO = '#fdf7ec';
const AMBAR_TEXTO = '#7a5b12';
const ROJO = '#c0392b';

const fmtFecha = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return '—'; }
};
const fmtMonto = (n) => Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Lee lo que escribio la persona. Acepta lo que se pega desde Aspel ("$12,500.50").
function leerImporte(txt) {
  const limpio = String(txt || '').replace(/[\s,$]/g, '');
  if (!limpio) return null;
  const n = Number(limpio);
  if (!Number.isFinite(n) || n <= 0) return NaN;
  return Math.round(n * 100) / 100;
}

export default function CapturaImportes() {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [borrador, setBorrador] = useState({});   // { [evento_id]: texto }
  const [estado, setEstado] = useState({});       // { [evento_id]: 'guardando'|'ok'|'error' }
  const [errores, setErrores] = useState({});     // { [evento_id]: mensaje }
  const [capturados, setCapturados] = useState(0);
  const { isMobile } = useResponsive();
  const campos = useRef({});                      // refs de los inputs, para saltar con Enter
  const guardadoRef = useRef({});                 // ultimo valor GUARDADO por evento

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/vigilante/sin-importe?tope=500`, { headers: authHeaders() });
      const d = await r.json().catch(() => null);
      if (!r.ok || !d || !d.ok) {
        throw new Error((d && d.error) || (r.status === 403
          ? 'No tienes permiso para capturar importes de facturas.'
          : 'No se pudo cargar la lista.'));
      }
      setLista(d.lista || []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar la lista.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Guarda UN renglon. Se llama al salir del campo (onBlur) y con Enter.
  const guardar = useCallback(async (fila) => {
    const id = fila.evento_id;
    const texto = borrador[id];
    // Nada escrito, o lo mismo que ya se guardo: no se molesta al servidor.
    if (texto === undefined || String(texto).trim() === '') return;
    if (guardadoRef.current[id] === String(texto).trim()) return;

    const importe = leerImporte(texto);
    if (Number.isNaN(importe) || importe === null) {
      setErrores(e => ({ ...e, [id]: 'Escribe un numero mayor que cero.' }));
      setEstado(s => ({ ...s, [id]: 'error' }));
      return;
    }

    setErrores(e => { const x = { ...e }; delete x[id]; return x; });
    setEstado(s => ({ ...s, [id]: 'guardando' }));
    try {
      const r = await fetch(
        `${API_BASE}/api/oportunidades/${fila.oportunidad_id}/flujo/registro/${id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ importe }),
        }
      );
      const d = await r.json().catch(() => null);
      if (!r.ok || !d || !d.ok) throw new Error((d && d.error) || 'No se pudo guardar.');

      guardadoRef.current[id] = String(texto).trim();
      setEstado(s => ({ ...s, [id]: 'ok' }));
      setCapturados(n => n + 1);
      // El renglon se va, pero no de golpe: 1,2 s en verde para que se vea que entro.
      setTimeout(() => {
        setLista(l => l.filter(f => f.evento_id !== id));
      }, 1200);
    } catch (e) {
      setErrores(er => ({ ...er, [id]: e.message || 'No se pudo guardar.' }));
      setEstado(s => ({ ...s, [id]: 'error' }));
    }
  }, [borrador]);

  // Enter = guardar y saltar al siguiente campo. Es el gesto que hace que esto
  // se pueda terminar de corrido sin tocar el raton.
  const alPulsar = (e, fila, indice) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    guardar(fila);
    const siguiente = lista[indice + 1];
    if (siguiente && campos.current[siguiente.evento_id]) {
      campos.current[siguiente.evento_id].focus();
    }
  };

  const pendientes = lista.length;
  const sinFolio = lista.filter(f => !f.recuperable).length;
  const sospechosos = lista.filter(f => f.sospecha_folio).length;

  // ---------- Cabecera ----------
  const cabecera = (
    <div style={{ marginBottom: 16 }}>
      <h2 style={{ fontFamily: PP, fontSize: isMobile ? 19 : 22, fontWeight: 700, color: OSCURO, margin: '0 0 6px' }}>
        Capturar importes de facturas
      </h2>
      <p style={{ fontFamily: PP, fontSize: 13.5, color: GRIS, margin: 0, lineHeight: 1.5, maxWidth: 780 }}>
        Estos expedientes se facturaron sin cotización, así que no quedó registrado cuánto valieron.
        Busca cada folio en Aspel y escribe el importe. <strong>Se guarda solo al salir del campo</strong>,
        y el renglón desaparece en cuanto entra. Puedes ir con el tabulador y con Enter, sin tocar el ratón.
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
        <Pastilla color={AZUL} etiqueta="Faltan por capturar" valor={pendientes} />
        {capturados > 0 && <Pastilla color={VERDE} etiqueta="Capturados en esta sesión" valor={capturados} />}
        {sospechosos > 0 && <Pastilla color={AMBAR_TEXTO} etiqueta="Folios que no encajan" valor={sospechosos} />}
        {sinFolio > 0 && <Pastilla color={GRIS_CLARO} etiqueta="Sin folio (no recuperables)" valor={sinFolio} />}
      </div>
    </div>
  );

  if (cargando) {
    return <div style={{ padding: isMobile ? 16 : 24, fontFamily: PP, color: GRIS }}>Cargando la lista…</div>;
  }
  if (error) {
    return (
      <div style={{ padding: isMobile ? 16 : 24, fontFamily: PP }}>
        {cabecera}
        <div style={{ border: `1px solid ${ROJO}`, background: '#fdecea', color: ROJO, borderRadius: 10, padding: 14, fontSize: 13.5 }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 16 : 24, fontFamily: PP }}>
      {cabecera}

      {lista.length === 0 ? (
        <div style={{
          border: `1px solid ${BORDE}`, background: '#f3fbf6', borderRadius: 12,
          padding: 24, textAlign: 'center', color: VERDE, fontSize: 15, fontWeight: 600,
        }}>
          No queda ninguna factura sin importe. Todo capturado.
        </div>
      ) : (
        <Tabla
          lista={lista}
          isMobile={isMobile}
          borrador={borrador}
          setBorrador={setBorrador}
          estado={estado}
          errores={errores}
          campos={campos}
          guardar={guardar}
          alPulsar={alPulsar}
        />
      )}
    </div>
  );
}

function Pastilla({ color, etiqueta, valor }) {
  return (
    <div style={{
      border: `1px solid ${BORDE}`, borderRadius: 10, padding: '8px 12px',
      background: '#fff', display: 'flex', alignItems: 'baseline', gap: 7,
    }}>
      <span style={{ fontSize: 19, fontWeight: 700, color }}>{valor}</span>
      <span style={{ fontSize: 12, color: GRIS }}>{etiqueta}</span>
    </div>
  );
}

// ============================================
// LA TABLA
// ============================================
// En celular NO se usa <table>: a 360 px una tabla de 5 columnas obliga a arrastrar de
// lado, y esta pantalla se usa sentado con Aspel, asi que la version de telefono solo
// tiene que ser legible, no comoda. Se pinta como tarjetas apiladas.
function Tabla({ lista, isMobile, borrador, setBorrador, estado, errores, campos, guardar, alPulsar }) {
  // Cabecera de grupo cuando cambia el cliente: asi se ve de un vistazo el bloque
  // que toca buscar en Aspel.
  let clienteAnterior = null;

  const filas = lista.map((f, i) => {
    const nuevoCliente = f.cliente !== clienteAnterior;
    clienteAnterior = f.cliente;
    return { f, i, nuevoCliente };
  });

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filas.map(({ f, i, nuevoCliente }) => (
          <React.Fragment key={f.evento_id}>
            {nuevoCliente && <TituloCliente nombre={f.cliente} />}
            <div style={{
              border: `1px solid ${estado[f.evento_id] === 'ok' ? VERDE : BORDE}`,
              borderRadius: 10, padding: 12, background: estado[f.evento_id] === 'ok' ? '#f3fbf6' : '#fff',
            }}>
              <div style={{ fontSize: 12, color: GRIS_CLARO, marginBottom: 2 }}>
                {f.folio} · {fmtFecha(f.facturado_at)}
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: OSCURO, marginBottom: 6 }}>{f.asunto}</div>
              <Folio fila={f} />
              <div style={{ marginTop: 8 }}>
                <CampoImporte
                  fila={f} indice={i} campos={campos} borrador={borrador} setBorrador={setBorrador}
                  estado={estado} guardar={guardar} alPulsar={alPulsar}
                />
                <Mensaje fila={f} estado={estado} errores={errores} />
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div style={{ border: `1px solid ${BORDE}`, borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: PP }}>
        <thead>
          <tr style={{ background: '#fafbfc' }}>
            <Th ancho="13%">Expediente</Th>
            <Th ancho="27%">Asunto</Th>
            <Th ancho="10%">Facturado</Th>
            <Th ancho="28%">Folio en Aspel</Th>
            <Th ancho="22%">Importe</Th>
          </tr>
        </thead>
        <tbody>
          {filas.map(({ f, i, nuevoCliente }) => (
            <React.Fragment key={f.evento_id}>
              {nuevoCliente && (
                <tr>
                  <td colSpan={5} style={{ padding: 0 }}><TituloCliente nombre={f.cliente} enTabla /></td>
                </tr>
              )}
              <tr style={{
                borderTop: `1px solid ${BORDE}`,
                background: estado[f.evento_id] === 'ok' ? '#f3fbf6' : 'transparent',
                transition: 'background 200ms',
              }}>
                <Td><span style={{ fontSize: 12.5, color: GRIS, whiteSpace: 'nowrap' }}>{f.folio}</span></Td>
                <Td><span style={{ fontSize: 13, color: OSCURO }}>{f.asunto}</span></Td>
                <Td><span style={{ fontSize: 12.5, color: GRIS_CLARO, whiteSpace: 'nowrap' }}>{fmtFecha(f.facturado_at)}</span></Td>
                <Td><Folio fila={f} /></Td>
                <Td>
                  <CampoImporte
                    fila={f} indice={i} campos={campos} borrador={borrador} setBorrador={setBorrador}
                    estado={estado} guardar={guardar} alPulsar={alPulsar}
                  />
                  <Mensaje fila={f} estado={estado} errores={errores} />
                </Td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TituloCliente({ nombre, enTabla }) {
  return (
    <div style={{
      background: '#f1f5f9', padding: enTabla ? '7px 12px' : '8px 12px',
      fontSize: 12.5, fontWeight: 700, color: AZUL, fontFamily: PP,
      borderRadius: enTabla ? 0 : 8, letterSpacing: 0.2,
    }}>
      {nombre}
    </div>
  );
}

const Th = ({ children, ancho }) => (
  <th style={{
    width: ancho, textAlign: 'left', padding: '10px 12px', fontSize: 11.5,
    fontWeight: 700, color: GRIS, textTransform: 'uppercase', letterSpacing: 0.4,
  }}>{children}</th>
);
const Td = ({ children }) => (
  <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>{children}</td>
);

// El folio tal cual se tecleo, con su aviso si no encaja con el patron de Beep.
function Folio({ fila }) {
  if (!fila.recuperable) {
    return (
      <div>
        <div style={{ fontSize: 13, color: GRIS_CLARO, fontStyle: 'italic' }}>
          {fila.factura_folio}
        </div>
        <div style={{ fontSize: 11.5, color: GRIS_CLARO, marginTop: 2 }}>
          Sin número: no hay nada que buscar en Aspel.
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 13, color: OSCURO, fontWeight: 600, wordBreak: 'break-word' }}>
        {fila.factura_folio}
      </div>
      {fila.sospecha_folio && (
        <div style={{
          marginTop: 4, background: AMBAR_FONDO, border: `1px solid ${AMBAR_BORDE}`,
          borderRadius: 7, padding: '4px 8px', fontSize: 11.5, color: AMBAR_TEXTO, lineHeight: 1.35,
        }}>
          ⚠️ {fila.sospecha_folio}
        </div>
      )}
    </div>
  );
}

function CampoImporte({ fila, indice, campos, borrador, setBorrador, estado, guardar, alPulsar }) {
  const id = fila.evento_id;
  const st = estado[id];
  return (
    <input
      ref={(el) => { campos.current[id] = el; }}
      type="text"
      inputMode="decimal"
      value={borrador[id] !== undefined ? borrador[id] : ''}
      onChange={(e) => setBorrador(b => ({ ...b, [id]: e.target.value.slice(0, 16) }))}
      onBlur={() => guardar(fila)}
      onKeyDown={(e) => alPulsar(e, fila, indice)}
      disabled={st === 'guardando' || st === 'ok'}
      placeholder="0.00"
      aria-label={`Importe de la factura del expediente ${fila.folio}`}
      style={{
        width: '100%', boxSizing: 'border-box', minHeight: 44, padding: '10px 12px',
        borderRadius: 8, fontFamily: PP, fontSize: 14, textAlign: 'right',
        border: `1px solid ${st === 'error' ? ROJO : st === 'ok' ? VERDE : BORDE}`,
        background: st === 'ok' ? '#eaf7f0' : st === 'guardando' ? '#f4f5f6' : '#fff',
        color: OSCURO,
      }}
    />
  );
}

function Mensaje({ fila, estado, errores }) {
  const id = fila.evento_id;
  const st = estado[id];
  if (errores[id]) {
    return <div style={{ fontSize: 11.5, color: ROJO, marginTop: 4, lineHeight: 1.35 }}>{errores[id]}</div>;
  }
  if (st === 'guardando') {
    return <div style={{ fontSize: 11.5, color: GRIS_CLARO, marginTop: 4 }}>Guardando…</div>;
  }
  if (st === 'ok') {
    return <div style={{ fontSize: 11.5, color: VERDE, marginTop: 4, fontWeight: 600 }}>✓ Guardado</div>;
  }
  return null;
}

export { leerImporte, fmtMonto };
